import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { fetchIndicatorsList, uploadFile, deleteIndicatorsData } from '../indicatorsApi';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../app/store';
import { NOTIFICATION_TYPES, ROLES } from '../../../utils/constant';
import { Button, Col, Input, Popover, Row, notification } from 'antd';
import { CloseOutlined, UploadOutlined, FilterFilled, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import FileUploadPopup from './components/FileUploadPopup';
import IndicatorTableComponent from './components/IndicatorTable';
import { ColumnsType } from 'antd/es/table';
import IndTemplateBreadcrumb from './components/IndTemplateBreadcrumb';
import FilterContent from './components/FilterContent';
import React from 'react';
import DeletePopup from './components/DeletePopup';
import { GetExcelExportWorkSheet } from './components/GetExcelExportWorkSheet';
import { GetTempTableColumns } from './components/GetTempTableColumns';
import * as XLSX from 'xlsx';
import { resetFilters, setFilters } from './components/slice/filterSlice';

interface TableDataType {
    srno: number;
    category: string;
    dataIndex: string;
    [key: string]: any;
}

const currentYear = new Date().getFullYear();
const yearRangeIndicatorIds = [11, 15, 9, 14];
type Filters = {
    fromYear: number;
    toYear: number;
};

const Indicators: React.FC = () => {
    const { indicatorId } = useParams(); // This will give you the id from the URL
    const { t } = useTranslation();
    // const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(100);
    const loginUser = useSelector((state: RootState) => state.auth.loginUser);
    const [searchQuery, setSearchQuery] = useState("");
    const { indicatorsData } = useSelector((state: RootState) => state.indicatorsData);
    const [columns, setColumns] = useState<ColumnsType<TableDataType>>([]);
    const indicatorIds = [11, 13, 15, 16, 9, 14];
    const [visible, setVisible] = useState<boolean>(false);
    const [filteredData, setFilteredData] = useState<TableDataType[]>([]);
    const defaultFromYear = yearRangeIndicatorIds.includes(parseInt(indicatorId || '0'))
        ? currentYear - 4
        : currentYear;

    const filters = useSelector((state: RootState) => state.filters[indicatorId ? indicatorId : '0'] || { fromYear: defaultFromYear, toYear: currentYear });
    const [tableData, setTableData] = useState<any[]>([]);
    const [excelWorkSheet, setExcelWorkSheet] = useState<XLSX.WorkSheet>([]);

    const previousFilters = useRef<Filters>(filters);
    const previousIndicatorId = useRef<string | null>(null);
    const [tableKey, setTableKey] = useState(0); // Add a key state for the table
    useEffect(() => {
        // This effect runs every time 'filters' state changes
        previousFilters.current = filters;
    }, [filters]);

    useEffect(() => {
        // This effect runs every time 'filters' state changes
        fetchIndicatorListData(filters.fromYear, filters.toYear);
    }, [filters.fromYear, indicatorId]);

    useEffect(() => {
        // Extract the current indicatorId from the URL
        const currentIndicatorId = location.pathname.split('/').pop();
        if (currentIndicatorId) {
            // Check if the indicatorId has changed
            if (previousIndicatorId.current && previousIndicatorId.current !== currentIndicatorId) {
                // Clear data for the old indicatorId from localStorage
                localStorage.removeItem(`filter_${previousIndicatorId.current}`);
                dispatch(resetFilters(previousIndicatorId.current));
            }

            // Update the ref to track the current indicatorId
            previousIndicatorId.current = currentIndicatorId;
        }
    }, [location.pathname, dispatch]);

    useEffect(() => {
        const fetchWorkSheet = async () => {
            if (indicatorId != undefined) {
                const ws = await GetExcelExportWorkSheet(parseInt(indicatorId), t, tableData, filters.fromYear, filters.toYear);
                setExcelWorkSheet(ws);
            }
        };
        fetchWorkSheet();
    }, [t, tableData]);

    useEffect(() => {
        const fetchColumns = async () => {
            if (indicatorId != undefined) {

                const cols = await GetTempTableColumns(parseInt(indicatorId), t, filters.fromYear, filters.toYear);
                setColumns(cols);
                setTableKey((prevKey) => prevKey + 1); // Update table key
            }
        };
        fetchColumns();
    }, [t, tableData, indicatorId]);


    useEffect(() => {
        const dataWithIds = indicatorsData.map((item, index) => ({
            ...item,
            srNo: index + 1, // Assign incremental IDs
        }));
        setTableData(dataWithIds);
    }, [indicatorsData, filters.fromYear]);


    useEffect(() => {
        // Filter data based on search query
        const filtered = tableData?.filter((data) =>
            Object.values(data).some((val) =>
                val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
        setFilteredData(filtered || []);
    }, [indicatorsData, searchQuery]);

    const fetchIndicatorListData = (fromYear: number, toYear: number) => {
        if (indicatorId && indicatorIds.includes(parseInt(indicatorId))) {
            const finalObj = {
                page: 1,
                id: indicatorId,
                startYear: fromYear, // Use currentYear as a default if fromYearVal is undefined
                endYear: toYear
            };

            dispatch(fetchIndicatorsList(finalObj));
            // Store the filters in localStorage
            if (indicatorId) {
                const filters = {
                    indicatorId,
                    fromYear,
                    toYear
                };
                localStorage.setItem(`filter_${indicatorId}`, JSON.stringify(filters)); // Persist in localStorage

                dispatch(setFilters(filters));
            }

            setSearchQuery("");
            setFilteredData(tableData);
        }
    };


    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    // const handleFileDownload = (indicatorId: string) => {
    //     dispatch(downloadFile(parseInt(indicatorId)));
    // }
    const handleSearch = (value: string) => {
        setSearchQuery(value);
        setCurrentPage(1);  // Reset to page 1 for new search
    };

    const handleFileUpload = (file: any, indicatorId: number, year: number) => {
        // console.log(localStorage.getItem('selectedKey'));
        const nodeId = localStorage.getItem('selectedKey') || "";
        if (file) {
            const formData = new FormData();
            formData.append('file', file); // Use `originFileObj` for the actual file object
            formData.append('id', indicatorId.toString());
            formData.append('startYear', year.toString());
            formData.append('nodeId', nodeId.toString());

            // // Only append fromYear and toYear if they are defined
            // if (fromYear !== undefined) {
            //     formData.append('fromYear', fromYear.toString());
            // }
            // if (toYear !== undefined) {
            //     formData.append('toYear', toYear.toString());
            // }
            dispatch(uploadFile(formData))
                .unwrap()
                .then((response) => {
                    if (response?.status === 'success') {
                        notification[NOTIFICATION_TYPES.SUCCESS]({
                            message: t('success'),
                            description: response?.message,
                        });
                        fetchIndicatorListData(response.data.startYear, response.data.endYear);
                    } else {
                        // notification[NOTIFICATION_TYPES.ERROR]({
                        //     message: t('error'),
                        //     description: response?.message,
                        // });
                    }
                })
                .catch((error) => {
                    if (error.response?.status === 422) {
                    }
                });

        }

    }
    const handleContinue = (file: any, year?: number) => {
        if (indicatorId != undefined) {
            const yearToPass = year || currentYear; // Set default year if undefined

            if (indicatorId === '13' || indicatorId === '16') {
                // Pass file and year if indicatorId is 13 or 16
                handleFileUpload(file, parseInt(indicatorId), yearToPass);
            } else {
                // Pass only file and indicatorId otherwise
                handleFileUpload(file, parseInt(indicatorId), yearToPass);
            }
        }
        setIsModalVisible(false);
    };


    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleApply = (fromYear: number, toYear: number) => {
        setVisible(false); // Close the popover after applying filters
        fetchIndicatorListData(fromYear, toYear);
    };

    const onDeleteAll = () => {
        const finalObj = {
            id: indicatorId,
        };
        handleDelete(finalObj);
    }
    const onDelete = (startYear: string, endYear: string) => {
        const finalObj = {
            id: indicatorId,
            startYear: startYear,
            endYear: endYear
        };
        handleDelete(finalObj);
    }

    const handleDelete = (finalObj: any) => {
        dispatch(deleteIndicatorsData(finalObj))
            .unwrap()
            .then((response) => {
                if (response?.status === 'success') {
                    notification[NOTIFICATION_TYPES.SUCCESS]({ message: t('success'), description: response?.message });
                    fetchIndicatorListData(filters.fromYear, filters.toYear);
                }
            });
    }

    const exportToExcel = () => {

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, excelWorkSheet, 'Sheet1');
        const retrievedArray = JSON.parse(localStorage.getItem('indicatorMenu') || '[]');
        const indId = indicatorId ? parseInt(indicatorId) : 0;
        const indicator = retrievedArray?.find((ind: any) => ind.id === indId);

        const fileName = () => {
            switch (indicatorId ? parseInt(indicatorId) : 9) {
                case 9:
                    return 'Skill_Development_Molsw.xlsx';
                case 11:
                    return 'Entrance_Exit_Data_Foreign_Nationals_MoPS.xlsx';
                case 13:
                    return 'Family_Registration_MoHA.xlsx';
                case 14:
                    return 'Total_Statistics_Molsw.xlsx';
                case 15:
                    return 'Entrance_Exit_Data_Lao_Nationals_MoPS.xlsx';
                case 16:
                    return 'Statistics_of_Lao_Population_MoHA.xlsx';
                default:
                    return 'Entrance_Exit_Data_Foreign_Nationals_MoPS.xlsx';
            }
        }

        // Write the Excel file
        XLSX.writeFile(wb, fileName());
        notification[NOTIFICATION_TYPES.SUCCESS]({ message: t('success'), description: `${t('excel.download.msg1')} (${indicator.name}) ${t('excel.download.msg2')}` });

    };
    return (
        <>

            <div>
                <Row className='mainTextArea' style={{ width: '100%' }}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                        <Row gutter={[16, 16]} style={{ borderBottom: '1px solid #CCC', marginBottom: '10px', paddingBottom: '10px' }}>
                            <Col xs={24} sm={24} md={12} lg={12} xl={12} >
                                <IndTemplateBreadcrumb separator='>' indicatorId={indicatorId ? parseInt(indicatorId) : 13} />
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='actionButHolder' style={{ padding: '8px 0 0' }}>
                                <div style={{ paddingBottom: '10px 0' }}>
                                    {/* {(loginUser?.user?.role_id !== ROLES.READ_ONLY_USER || loginUser?.user?.role_id !== ROLES.DATA_COLLECTOR) && */}
                                    {!(loginUser?.user?.role_id === ROLES.DATA_COLLECTOR || loginUser?.user?.role_id === ROLES.READ_ONLY_USER) && (

                                        <DeletePopup
                                            onDelete={(deleteAll: boolean, fromYear: number, toYear: number) => {
                                                if (deleteAll) {
                                                    onDeleteAll();
                                                    // Handle complete data deletion
                                                } else if (fromYear && toYear) {
                                                    onDelete(fromYear.toString(), toYear.toString());
                                                }
                                            }}

                                            indicatorId={indicatorId}
                                            indicatorIds={indicatorIds}
                                        />
                                    )}

                                    <div style={{ paddingBottom: '10px 0' }}>
                                        <Popover
                                            content={<FilterContent
                                                indicatorId={indicatorId ? indicatorId : '1'}
                                                onApply={handleApply}
                                                fromYearDefault={filters.fromYear}
                                                toYearDefault={filters.toYear}
                                            />}
                                            trigger="click"
                                            open={visible}
                                            onOpenChange={setVisible}
                                            style={{ width: '300px' }}
                                            placement="bottom"
                                        >
                                            <Button type="primary"
                                                disabled={indicatorId && indicatorIds.includes(parseInt(indicatorId)) ? false : true}

                                            >
                                                <FilterFilled /> {t('btn.filter')}
                                            </Button>
                                        </Popover>
                                    </div>
                                    {loginUser?.user?.role_id !== ROLES.READ_ONLY_USER &&
                                        <>
                                            <Button type="primary" onClick={showModal}>
                                                <UploadOutlined /> {t('upload.data')}
                                            </Button>
                                            <Button type="primary" onClick={exportToExcel}
                                                disabled={indicatorId && indicatorIds.includes(parseInt(indicatorId)) ? false : true}
                                            >
                                                <DownloadOutlined /> {t('export.to.excel.btn')}
                                            </Button>
                                            {/* <Button type="primary"
                                                onClick={() => indicatorId !== undefined && handleFileDownload(indicatorId)}><DownloadOutlined /> {t('download.template')}
                                            </Button> */}
                                        </>}
                                    <Input placeholder={t('search.placeholder')}
                                        className='searchInput'
                                        value={searchQuery}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        suffix={<CloseOutlined onClick={() => handleSearch("")} />}
                                        prefix={<SearchOutlined />}
                                        disabled={indicatorId && indicatorIds.includes(parseInt(indicatorId)) ? false : true}
                                    />

                                    {/* } */}
                                </div>
                                {/* } */}
                            </Col>

                        </Row>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                        {indicatorId && indicatorIds.includes(parseInt(indicatorId)) ?

                            <div className='tableHolder IndicatorTableHolder'>
                                <IndicatorTableComponent
                                    key={tableKey} columns={columns}
                                    data={searchQuery ? filteredData : tableData}
                                    showSizeChanger={true}
                                    pageSize={pageSize}
                                    total={indicatorsData?.length}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    indicatorId={indicatorId}
                                />


                            </div>
                            :
                            <h3 style={{ textAlign: 'center' }}>{t("data-not-found")}</h3>
                        }
                    </Col>
                </Row>
                <FileUploadPopup
                    visible={isModalVisible}
                    onCancel={handleCancel}
                    onContinue={handleContinue}
                    indicatorId={indicatorId ? indicatorId : '1'}
                />
            </div>

        </>
    );

};

export default Indicators;
